import classNames from "classnames";
import React, {
  CSSProperties,
  ChangeEvent,
  FC,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./TextInput.module.scss";

interface TextInputProps {
  id: string;
  label?: string;
  name: string;
  type?: "text" | "email";
  required?: boolean;
  value?: string;
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
  onChange?: (value: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  disabled?: boolean;
  readOnly?: boolean;
  hasError?: boolean;
  infoText?: string;
  errorText?: string;
  showMapButton?: boolean;
  onMapButtonClick?: () => void;
  suggestions?: any[];
  onSuggestionClick?: (suggestion: any) => void;
  showSuggestions?: boolean;
}

const TextInput: FC<
  React.PropsWithChildren<
    React.PropsWithChildren<React.PropsWithChildren<TextInputProps>>
  >
> = (props: TextInputProps) => {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (props.value) {
      setValue(props?.value);
      if (inputRef.current) {
        inputRef.current.value = props.value;
      }
    } else {
      setValue("");
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  }, [props.value]);

  const disabledClass = classNames({
    [styles.disabled]: props.disabled,
  });

  const inputClasses = classNames({
    [styles.inputWrapper]: !props.showMapButton,
    [styles.inputWithButton]: props.showMapButton,
    [styles.hasError]: props.hasError,
  });

  return (
    <div className={styles.container} style={props.bodyStyle}>
      <div className="df fd">
        {props.label && (
          <label htmlFor={props.id} className={disabledClass}>
            {props.label}
          </label>
        )}
      </div>

      <div className={inputClasses}>
        {props.showMapButton ? (
          <div>
            <input
              id={props.id}
              type={props.type}
              required={props.required}
              name={props.name}
              ref={inputRef}
              autoComplete="off"
              style={props.style}
              onBlur={() => (props.onBlur ? props.onBlur() : null)}
              onFocus={() => (props.onFocus ? props.onFocus() : null)}
              onKeyDown={(e) => (props.onKeyDown ? props.onKeyDown(e) : null)}
              onChange={(e) => (props.onChange ? props.onChange(e) : null)}
              placeholder={props.placeholder}
              defaultValue={value}
              disabled={props.disabled}
              readOnly={props.readOnly}
            />
            <button
              type="button"
              className={styles.mapButton}
              onClick={props.onMapButtonClick}
              disabled={props.disabled}
            >
              📍
            </button>
          </div>
        ) : (
          <input
            id={props.id}
            type={props.type}
            required={props.required}
            name={props.name}
            ref={inputRef}
            autoComplete="off"
            style={props.style}
            onBlur={() => (props.onBlur ? props.onBlur() : null)}
            onFocus={() => (props.onFocus ? props.onFocus() : null)}
            onKeyDown={(e) => (props.onKeyDown ? props.onKeyDown(e) : null)}
            onChange={(e) => (props.onChange ? props.onChange(e) : null)}
            placeholder={props.placeholder}
            defaultValue={value}
            disabled={props.disabled}
            readOnly={props.readOnly}
          />
        )}

        {props.showSuggestions &&
          props.suggestions &&
          props.suggestions.length > 0 && (
            <div className={styles.suggestionsContainer}>
              {props.suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={styles.suggestion}
                  onClick={() =>
                    props.onSuggestionClick &&
                    props.onSuggestionClick(suggestion)
                  }
                >
                  {suggestion.description}
                </div>
              ))}
            </div>
          )}

        {props.hasError && props.errorText && (
          <div className={styles.errorText}>{props.errorText}</div>
        )}
      </div>
    </div>
  );
};

TextInput.defaultProps = {
  disabled: false,
  hasError: false,
  type: "text",
};

export default TextInput;
